// src/services/car.js
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const API = `${API_BASE}/api/v1`;
const cache = new Map();
const CACHE_TTL = 30 * 1000;

function getCacheKey(url, params) {
  return `${url}?${JSON.stringify(params)}`;
}

function getFromCache(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

const apiClient = axios.create({
  baseURL: API,
  timeout: 60000,
});

let activeCarSearchController = null;

export async function searchCarsAdvanced(filters = {}) {
  if (activeCarSearchController) {
    activeCarSearchController.abort();
  }
  activeCarSearchController = new AbortController();

  const params = {
    per_page: filters.per_page || 12,
    page: filters.page || 1,
    ...filters,
  };

  const cacheKey = getCacheKey("/cars", params);
  const cached = getFromCache(cacheKey);
  if (cached) {
    console.log("⚡ Cache hit");
    return cached;
  }

  try {
    const res = await apiClient.get("/cars", {
      params,
      signal: activeCarSearchController.signal,
    });
    setCache(cacheKey, res.data);
    return res.data;
  } catch (error) {
    if (
      error.name === "CanceledError" ||
      error.code === "ERR_CANCELED" ||
      error.name === "AbortError"
    ) {
      console.log("🚫 Request cancelled (stale)");
      return null;
    }
    console.error("Error searching cars:", error);
    throw error;
  }
}

export async function fetchPageOfCars(page = 1, perPage = 10) {
  const params = { page, per_page: perPage };
  const cacheKey = getCacheKey("/cars_featured", params);
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const controller = new AbortController();
  try {
    const res = await apiClient.get("/cars", {
      params,
      signal: controller.signal,
    });
    setCache(cacheKey, res.data);
    return res.data;
  } catch (error) {
    if (
      error.name === "CanceledError" ||
      error.code === "ERR_CANCELED" ||
      error.name === "AbortError"
    ) {
      return null;
    }
    console.error("Error fetching page of cars:", error);
    throw error;
  }
}

export async function fetchCar(id) {
  const cacheKey = getCacheKey(`/cars/${id}`, {});
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  try {
    const res = await apiClient.get(`/cars/${id}`);
    setCache(cacheKey, res.data);
    return res.data;
  } catch (error) {
    console.error("Error fetching car:", error);
    throw error;
  }
}

export async function fetchSearchSuggestions(searchTerm, limit = 8) {
  try {
    if (!searchTerm || searchTerm.trim() === "") return [];
    const res = await apiClient.get("/cars/search_suggestions", {
      params: { q: searchTerm.trim(), limit },
    });
    return res.data.suggestions || [];
  } catch (error) {
    console.error("Error fetching suggestions:", error);
    return [];
  }
}

export async function fetchCars(params = {}) {
  return searchCarsAdvanced(params);
}

export async function fetchAllCars() {
  try {
    const res = await apiClient.get("/cars", { params: { per_page: 1000 } });
    return res.data.data || [];
  } catch (error) {
    return [];
  }
}

export async function fetchAvailableBrands() {
  try {
    const res = await apiClient.get("/cars", { params: { per_page: 1000 } });
    const cars = res.data.data || [];
    return [...new Set(cars.map((c) => c.brand).filter(Boolean))].sort();
  } catch (error) {
    return [];
  }
}

export async function fetchCarsByBrand(brandName, filters = {}) {
  try {
    const res = await apiClient.get("/cars", {
      params: { brand: brandName, per_page: 100, ...filters },
    });
    return res.data;
  } catch (error) {
    throw error;
  }
}

export async function fetchFeaturedCars(count = 4) {
  try {
    const res = await apiClient.get("/cars", {
      params: { page: 1, per_page: count },
    });
    return res.data;
  } catch (error) {
    console.error("Error fetching featured cars:", error);
    return null;
  }
}